import { useEffect, useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNowStrict } from 'date-fns/formatDistanceToNowStrict';
import { get, post } from '../lib/api';
import { IconButton, Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { EmptyState } from '../ui/misc';
import { cn } from '../lib/cn';
import { isImpersonating } from '../lib/impersonation';

type N = { id: number; title: string; body: string | null; link: string | null; createdAt: string; readAt: string | null };
type NPage = { items: N[]; nextCursor: string | null; unread: number };

export const NOTIF_KEY = ['notifications'] as const;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: NOTIF_KEY, queryFn: () => get<NPage>('/api/notifications'), refetchInterval: 300_000 }); // SSE (lib/events.ts) invalidates on change; this is only the safety net
  const read = useMutation({
    // Read state is the client's own: never changed while acting as them (§2i).
    mutationFn: async (body: { ids?: number[]; all?: true }) => (isImpersonating() ? undefined : post('/api/notifications/read', body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });
  const unread = q.data?.unread ?? 0;
  useEffect(() => {
    const nav2 = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    void (unread > 0 ? nav2.setAppBadge?.(unread) : nav2.clearAppBadge?.())?.catch?.(() => undefined);
  }, [unread]);
  return (
    <>
      <IconButton label={unread ? `Notifications, ${unread} unread` : 'Notifications'} badge={unread} onClick={() => setOpen(true)}>
        <Bell aria-hidden className="size-5" />
      </IconButton>
      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Notifications"
        footer={
          unread > 0 ? (
            <Button variant="outline" block onClick={() => read.mutate({ all: true })} loading={read.isPending}>
              Mark all as read
            </Button>
          ) : undefined
        }
      >
        {q.isPending ? (
          <p className="py-6 text-center text-muted">Loading…</p>
        ) : q.isError ? (
          <p className="py-6 text-center text-danger-ink">Couldn't load notifications.</p>
        ) : !q.data?.items.length ? (
          <EmptyState icon={BellOff} text="You're all caught up." />
        ) : (
          <ul className="-mx-2">
            {q.data.items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (!n.readAt) read.mutate({ ids: [n.id] });
                    setOpen(false);
                    if (n.link) nav(n.link);
                  }}
                  className="flex w-full items-start gap-3 rounded-lg px-2 py-3 text-left hover:bg-surface"
                >
                  <span aria-hidden className={cn('mt-2 size-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-primary')} />
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-[15px]', n.readAt ? 'text-ink-2' : 'font-semibold text-ink')}>
                      {!n.readAt && <span className="sr-only">Unread: </span>}
                      {n.title}
                    </span>
                    {n.body && <span className="block text-sm text-muted">{n.body}</span>}
                    <span className="block text-[13px] text-subtle">{formatDistanceToNowStrict(new Date(n.createdAt), { addSuffix: true })}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Sheet>
    </>
  );
}
